import { CompanyViewAs } from 'domain/companies/roleUtils';
import React, { ReactNode } from 'react';
import { PitchDTO } from 'types/company';
import SectionHeading from 'ui/elements/SectionHeading';
import HorizontalDictionary from 'ui/elements/dictionary/HorizontalDictionary';
import Card from 'ui/views/cards/Card';
import { formatCurrency, DEFAULT_CURRENCY } from 'util/currency';
import InlineEditableField from 'ui/elements/form/InlineEditableField';
import CurrencyInput from 'ui/elements/form/numbers/CurrencyInput';
import { companiesApi } from 'apis/CompanyAPI/companies/companiesApi';
import useLazyResource from 'util/resource/useLazyResource';
import useNotify from 'hooks/useNotify';
import currencyMap from 'util/currency';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';

interface Props {
  company: PitchDTO;
  reloadCompany: () => void;
  viewAs: CompanyViewAs;
}

interface CurrencyValue {
  value?: number;
  currency: string;
}

export default function RevenueAndValuationSection({ company, reloadCompany, viewAs }: Props) {
  const canViewValuation = viewAs !== 'Follower' && viewAs !== 'Visitor';
  const isAdminForCompany = viewAs === 'Admin';
  const notify = useNotify();
  const { setLastUpdated } = useCompanyLastUpdated(company.id);

  // Validation functions
  const validateCurrencyValue = (currencyValue: CurrencyValue, fieldName: string): string | null => {
    // Validate currency
    if (!Object.keys(currencyMap).includes(currencyValue.currency)) {
      return `Invalid currency selected for ${fieldName.toLowerCase()}`;
    }

    // Validate maximum value (less than 10^15)
    if (currencyValue.value !== null && currencyValue.value !== undefined) {
      if (currencyValue.value >= 1000000000000000) {
        return `${fieldName} must be less than 1 000 000 000 000 000`;
      }
    }

    return null;
  };

  const [updateCompany] = useLazyResource(
    (payload: { revenues?: number; revenuesCurrency?: string; valuation?: number; valuationCurrency?: string }) =>
      companiesApi.update(company.id, payload),
    {
      onSuccess: () => {
        reloadCompany();
        setLastUpdated(new Date());
        notify('success', 'Company information updated successfully');
      },
      onFailure: () => notify('error', 'Something unexpected happened when trying to update company information.'),
    },
  );

  if (!isAdminForCompany && !company.revenues.value && !company.valuation?.value) {
    return null;
  }

  const itemsToShow = [
    {
      key: 'Revenues',
      value: isAdminForCompany ? (
        <InlineEditableField<CurrencyValue>
          label="Add revenues"
          defaultValue={{
            value: company.revenues.value,
            currency: company.revenues.currency ?? DEFAULT_CURRENCY,
          }}
          onConfirm={currencyValue => {
            // Validate input
            const validationError = validateCurrencyValue(currencyValue, 'Revenues');
            if (validationError) {
              notify('error', validationError);
              return Promise.reject(new Error(validationError));
            }

            // Only update if values actually changed
            const hasChanged =
              currencyValue.value !== company.revenues.value ||
              currencyValue.currency !== (company.revenues.currency ?? DEFAULT_CURRENCY);

            if (!hasChanged) {
              return Promise.resolve();
            }

            return updateCompany({
              revenues: currencyValue.value,
              revenuesCurrency: currencyValue.currency,
            });
          }}
          inputWrapperStyle={{
            display: 'flex',
            minHeight: '45px', // Match CurrencyInput height
          }}
          renderInputComponent={(onChange, value) => (
            <CurrencyInput
              autoFocus
              name="revenues"
              value={value.value}
              currency={value.currency}
              onChange={v => onChange({ ...value, value: v as number | undefined })}
              onCurrencyChange={currency => onChange({ ...value, currency })}
              placeholder="1 000 000"
              nullable={true}
            />
          )}
          valueComponent={
            <span className="u-flex-align-center text-body">{formatCurrency(false, company.revenues)}</span>
          }
        />
      ) : (
        <span className="u-flex-align-center text-body">{formatCurrency(false, company.revenues)}</span>
      ),
    },
    canViewValuation
      ? {
          key: 'Valuation',
          value: isAdminForCompany ? (
            <InlineEditableField<CurrencyValue>
              label="Add valuation"
              defaultValue={{
                value: company.valuation?.value,
                currency: company.valuation?.currency ?? DEFAULT_CURRENCY,
              }}
              onConfirm={currencyValue => {
                // Validate input
                const validationError = validateCurrencyValue(currencyValue, 'Valuation');
                if (validationError) {
                  notify('error', validationError);
                  return Promise.reject(new Error(validationError));
                }

                // Only update if values actually changed
                const hasChanged =
                  currencyValue.value !== company.valuation?.value ||
                  currencyValue.currency !== (company.valuation?.currency ?? DEFAULT_CURRENCY);

                if (!hasChanged) {
                  return Promise.resolve();
                }

                return updateCompany({
                  valuation: currencyValue.value,
                  valuationCurrency: currencyValue.currency,
                });
              }}
              inputWrapperStyle={{
                display: 'flex',
                minHeight: '45px', // Match CurrencyInput height
              }}
              renderInputComponent={(onChange, value) => (
                <CurrencyInput
                  autoFocus
                  name="valuation"
                  value={value.value}
                  currency={value.currency}
                  onChange={v => onChange({ ...value, value: v as number | undefined })}
                  onCurrencyChange={currency => onChange({ ...value, currency })}
                  placeholder="10 000 000"
                  nullable={true}
                />
              )}
              valueComponent={
                <div>
                  <span className="u-flex-align-center text-body">{formatCurrency(false, company.valuation)}</span>
                  {isAdminForCompany && (
                    <div className="text-metadata u-quarter-spacing-top">
                      Valuation is only visible to company admins, board members and shareholders
                    </div>
                  )}
                </div>
              }
            />
          ) : (
            <span className="u-flex-align-center text-body">{formatCurrency(false, company.valuation)}</span>
          ),
        }
      : undefined,
  ].filter(it => it !== undefined) as { key: string; value: ReactNode }[];

  return (
    <div>
      <SectionHeading heading={canViewValuation ? 'Revenues and valuation' : 'Revenues'} addMobileMargin />
      <Card>
        <HorizontalDictionary columnCount={2}>{itemsToShow}</HorizontalDictionary>
      </Card>
    </div>
  );
}
